import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import "dayjs/locale/es";
import weekOfYear from "dayjs/plugin/weekOfYear";
import { PATH } from "../../redux/utils";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import ScrollDatePicker from "../../components/calendar/ScrollDatePicker";

dayjs.extend(isSameOrAfter);
dayjs.extend(weekOfYear);
dayjs.locale("es");

// ── API ───────────────────────────────────────────────────────────────────────
const authH = () => {
  const t = localStorage.getItem("tokenadmin") || localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const api = axios.create({ baseURL: PATH });

// ── Colores ───────────────────────────────────────────────────────────────────
const C = {
  primary:     "#0A539A", // color primario de la marca (navy) — antdTheme.js
  primaryHover:"#0D63B5",
  primarySoft: "#0A539A18",
  red:       "#bc004b",
  error:     "#ba1a1a",
  concluido:     "#20c997",
  errorSoft: "#ba1a1a12",
  amber:     "#c77700",
  amberSoft: "#c7770022",
  surface:   "#f8f9fa",
  surfaceLow:"#f3f4f5",
  surfaceMid:"#edeeef",
  surfaceHigh:"#e7e8e9",
  white:     "#ffffff",
  dark:      "#191c1d",
  muted:     "#6b717a",
  outline:   "#d0c5af",
};

// Mismo tratamiento de card que la página de detalle de conciliación
// (.laboral-exp-detail-card) — para que Inicio se sienta parte de la misma app.
const CARD_SHADOW = "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)";
const CARD_BORDER = "1px solid #e2e8f0";

// ── Color por ciudad — identifica cada ciudad de un vistazo en tablas y chips ──
const CITY_COLORS = {
  700:  "#4c56af", // Mexicali
  703:  "#bc004b", // Tijuana
  2633: "#0f9d78", // Puerto Peñasco
  2641: "#c77700", // San Luis R.C.
  699:  "#959efd", // Ensenada
};
const cityColor = (idCiudad) => CITY_COLORS[idCiudad] || C.muted;

// Sentinel para "Todos" en el selector de ciudad — agrega los datos de todas
// las ciudades que el usuario puede ver (no existe como id_ciudad real).
const ALL_CITIES = "all";

const VIEW_MODES = [{ key: "dia", label: "Día" }, { key: "semana", label: "Semana" }, { key: "mes", label: "Mes" }];

function getDateRange(date, mode) {
  const d = dayjs(date);
  if (mode === "dia")    return { desde: d.format("YYYY-MM-DD"), hasta: d.format("YYYY-MM-DD") };
  if (mode === "semana") return { desde: d.startOf("week").format("YYYY-MM-DD"), hasta: d.endOf("week").format("YYYY-MM-DD") };
  return { desde: d.startOf("month").format("YYYY-MM-DD"), hasta: d.endOf("month").format("YYYY-MM-DD") };
}

function navigateDate(date, mode, dir) {
  const d = dayjs(date);
  if (mode === "dia")    return d.add(dir, "day").format("YYYY-MM-DD");
  if (mode === "semana") return d.add(dir, "week").format("YYYY-MM-DD");
  return d.add(dir, "month").format("YYYY-MM-DD");
}

// Capitaliza la primera letra de cada palabra (día/mes), dejando conectores como "de" en minúscula.
function capitalizeDateWords(str) {
  const lower = new Set(["de", "y"]);
  return str.replace(/\p{L}+/gu, (word, offset) =>
    lower.has(word.toLowerCase()) && offset !== 0
      ? word
      : word.charAt(0).toUpperCase() + word.slice(1)
  );
}

function buildRangeLabel(date, mode) {
  const d = dayjs(date);
  let label;
  if (mode === "dia") {
    label = d.format("dddd D [de] MMMM YYYY");
  } else if (mode === "mes") {
    label = d.format("MMMM YYYY");
  } else {
    const start = d.startOf("week");
    const end   = d.endOf("week");
    label = start.month() === end.month()
      ? `${start.format("D")} – ${end.format("D [de] MMMM YYYY")}`
      : `${start.format("D MMM")} – ${end.format("D MMM YYYY")}`;
  }
  return capitalizeDateWords(label);
}
function uniqueByExpediente(arr) {
  const seen = new Set();
  return arr.filter((item) => {
    const key = item.expediente_format || item.expediente;
    if (!key) return true; // si no hay expediente, no lo bloquees
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
// Combina las respuestas de /home/panel de varias ciudades en una sola (para
// la opción "Todos" del selector de ciudad).
function mergePanelResults(results) {
  const byDate = (key) => (a, b) => new Date(a[key]) - new Date(b[key]);

  return {
    conciliacion_audiencias: results
      .flatMap((r) => r.conciliacion_audiencias ?? [])
      .sort(byDate("fecha_audiencia"))
      .slice(0, 100),
    convenios_pagos: results
      .flatMap((r) => r.convenios_pagos ?? [])
      .sort(byDate("fecha_pago"))
      .slice(0, 100),
    tribunal: {
      audiencias_periodo: results.reduce((s, r) => s + (r.tribunal?.audiencias_periodo ?? 0), 0),
      terminos_criticos:  results.reduce((s, r) => s + (r.tribunal?.terminos_criticos ?? 0), 0),
      audiencias: results
        .flatMap((r) => r.tribunal?.audiencias ?? [])
        .sort(byDate("fecha_audiencia"))
        .slice(0, 100),
      terminos: results
        .flatMap((r) => r.tribunal?.terminos ?? [])
        .sort(byDate("fecha_limite"))
        .slice(0, 100),
      convenios_pagos: results
        .flatMap((r) => r.tribunal?.convenios_pagos ?? [])
        .sort(byDate("fecha_pago"))
        .slice(0, 100),
    },
    desvinculaciones: results
      .flatMap((r) => r.desvinculaciones ?? [])
      .sort(byDate("fecha_solicitacion"))
      .slice(0, 100),
    periodo: results[0]?.periodo,
  };
}

// ── helpers ───────────────────────────────────────────────────────────────────
function fmtMonto(v) {
  if (v == null) return "";
  return `$${Number(v).toLocaleString("es-MX", { maximumFractionDigits: 2 })}`;
}

// Capitaliza cada palabra: "juan perez" → "Juan Perez"
const titleCase = (s) =>
  s ? s.split(" ").map((w) => w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : "").join(" ") : s;

const trabajadoresText = (r) =>
  Array.isArray(r?.trabajadores)
    ? r.trabajadores.map((t) => t?.nombre_trabajador).filter(Boolean).join(", ")
    : "";

// ── Badge helpers ─────────────────────────────────────────────────────────────
const badgeStyle = (bg, color) => ({
  display: "inline-block",
  background: bg, color,
  fontSize: 10, fontWeight: 700,
  padding: "2px 8px", borderRadius: 4,
  textTransform: "uppercase", letterSpacing: "0.05em",
  whiteSpace: "nowrap",
});

function StatusBadge({ status }) {
  const s = (status || "").toLowerCase();
  if (s === "convenio")     return <span style={badgeStyle(C.error, "#fff")}>CUMPLIMIENTO</span>;
  if (s === "diferimiento") return <span style={badgeStyle(C.surfaceMid, C.muted)}>DIFERIDO</span>;
  if (s === "no_conciliacion") return <span style={badgeStyle(C.surfaceMid, C.muted)}>SIN ACUERDO</span>;
  if (s === "activo")       return <span style={badgeStyle(C.primarySoft, C.primary)}>ACTIVO</span>;
  return <span style={badgeStyle(C.surfaceMid, C.muted)}>{status || "—"}</span>;
}

function EtapaBadge({ label }) {
  return <span style={badgeStyle(C.primarySoft, C.primary)}>{(label || "").toUpperCase()}</span>;
}

function TermBadge({ iso }) {
  if (!iso) return <span style={badgeStyle(C.surfaceMid, C.muted)}>—</span>;
  const d    = dayjs(iso);
  const now  = dayjs().startOf("day");
  const diff = d.diff(now, "day");
  if (diff === 0) return <span style={badgeStyle(C.error, "#fff")}>HOY</span>;
  if (diff <= 3)  return <span style={badgeStyle(C.error, "#fff")}>EN {diff} DÍA{diff !== 1 ? "S" : ""}</span>;
  return <span style={badgeStyle(C.surfaceMid, C.muted)}>{d.format("D MMM")}</span>;
}

function PagoBadge({ iso }) {
  if (!iso) return <span style={badgeStyle(C.surfaceMid, C.muted)}>—</span>;
  const d    = dayjs(iso);
  const now  = dayjs().startOf("day");
  const diff = d.diff(now, "day");        // negativo = pasado, 0 = hoy, positivo = futuro
  if (diff < 0)  return <span style={badgeStyle(C.concluido, "#fff")}>CONCLUIDO</span>;
  if (diff === 0) return <span style={badgeStyle(C.error, "#fff")}>HOY</span>;
  if (diff <= 3)  return <span style={badgeStyle(C.error, "#fff")}>EN {diff} DÍA{diff !== 1 ? "S" : ""}</span>;
  return <span style={badgeStyle(C.error, "#fff")}>{d.format("D MMM")}</span>;
}

// Alerta de proximidad para audiencias — solo se muestra cuando el día ya
// casi llega (hoy o dentro de los próximos 3 días); en cualquier otro caso
// no ocupa espacio en la fila.
function ProximoBadge({ iso }) {
  if (!iso) return null;
  const d    = dayjs(iso);
  const now  = dayjs().startOf("day");
  const diff = d.diff(now, "day");
  if (diff < 0 || diff > 3) return null;
  const label = diff === 0 ? "HOY" : `EN ${diff} DÍA${diff !== 1 ? "S" : ""}`;
  return (
    <div style={{ marginTop: 3 }}>
      <span style={badgeStyle(C.error, "#fff")}>{label}</span>
    </div>
  );
}
function uniqueBy(arr, keyFn) {
  const seen = new Map();
  for (const item of arr) {
    const key = keyFn(item);
    if (!seen.has(key)) {
      seen.set(key, item);
    }
  }
  return Array.from(seen.values());
}
// ── Count pill (tab Resumen) ──────────────────────────────────────────────────
function CountPill({ n, color, bg, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: "3px 14px", borderRadius: 999,
        background: n > 0 ? (hover ? color : bg) : C.surfaceHigh,
        color: n > 0 ? (hover ? "#fff" : color) : C.muted,
        fontWeight: 700, fontSize: 13,
        border: "none", cursor: n > 0 ? "pointer" : "default",
        transition: "all 0.15s", minWidth: 44,
      }}
    >
      {String(n).padStart(2, "0")}
    </button>
  );
}

// ── Date Navigation Bar ───────────────────────────────────────────────────────
function DateNavBar({ date, mode, onDateChange, onModeChange }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const label     = buildRangeLabel(date, mode);
  const weekNum   = dayjs(date).startOf("week").week();
  const isToday   = date === dayjs().format("YYYY-MM-DD");
  const showWeek  = mode === "semana";

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>

      {/* Mode chips */}
      <div style={{
        display: "flex", background: C.surfaceLow,
        borderRadius: 10, padding: 3,
        border: `1px solid ${C.outline}40`,
      }}>
        {VIEW_MODES.map((m) => (
          <button
            key={m.key}
            onClick={() => onModeChange(m.key)}
            style={{
              padding: "6px 18px", borderRadius: 8,
              background: mode === m.key ? C.primary : "transparent",
              color: mode === m.key ? "#fff" : C.muted,
              border: "none", fontWeight: 700, fontSize: 13,
              cursor: "pointer", transition: "background 0.2s ease, color 0.2s ease",
            }}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* Hoy — siempre en el DOM, entra/sale con fade + colapso de ancho */}
      <div style={{
        overflow: "hidden",
        maxWidth: isToday ? "0px" : "90px",
        opacity: isToday ? 0 : 1,
        transition: "max-width 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.2s ease",
        flexShrink: 0,
      }}>
        <button
          onClick={() => onDateChange(dayjs().format("YYYY-MM-DD"))}
          style={{
            padding: "7px 14px", borderRadius: 8,
            background: "transparent", color: C.muted,
            border: `1px solid ${C.outline}60`,
            fontWeight: 600, fontSize: 13, cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          Hoy
        </button>
      </div>

      {/* Nav bar: prev | date label | next */}
      <div style={{
        display: "flex", alignItems: "center",
        background: C.white, borderRadius: 10,
        border: `1px solid ${C.outline}50`,
        boxShadow: "0 1px 4px rgba(25,28,29,0.06)",
        overflow: "hidden",
      }}>
        <button
          onClick={() => onDateChange(navigateDate(date, mode, -1))}
          style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            width: 34, height: 36,
            background: "none", border: "none",
            borderRight: `1px solid ${C.outline}40`,
            cursor: "pointer", color: C.muted,
            transition: "background 0.15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = C.surfaceLow)}
          onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>chevron_left</span>
        </button>

        {/* Clickable date label — ancho mínimo para evitar saltos al cambiar texto */}
        <div style={{ position: "relative" }}>
          <button
            onClick={() => setPickerOpen(true)}
            style={{
              display: "flex", alignItems: "center", gap: 7,
              padding: "6px 14px", background: "none", border: "none",
              cursor: "pointer", transition: "background 0.15s",
              minWidth: 200,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = C.surfaceLow)}
            onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
          >
            <i className="fa-solid fa-calendar" style={{ fontSize: 15, color: C.primary }} />
            <span style={{
              fontSize: 13, fontWeight: 700, color: C.dark, whiteSpace: "nowrap",
              transition: "opacity 0.2s ease",
            }}>
              {label}
            </span>
          </button>

          {/* ScrollDatePicker invisible posicionado sobre el botón: el botón
              de arriba es el único trigger visible, esto solo aporta el panel. */}
          <ScrollDatePicker
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            value={dayjs(date)}
            onChange={(d) => {
              if (d) onDateChange(d.format("YYYY-MM-DD"));
              setPickerOpen(false);
            }}
            style={{
              position: "absolute",
              opacity: 0,
              pointerEvents: "none",
              width: 0,
              height: 0,
              top: "100%",
              left: 0,
            }}
          />
        </div>

        <button
          onClick={() => onDateChange(navigateDate(date, mode, 1))}
          style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            width: 34, height: 36,
            background: "none", border: "none",
            borderLeft: `1px solid ${C.outline}40`,
            cursor: "pointer", color: C.muted,
            transition: "background 0.15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = C.surfaceLow)}
          onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>chevron_right</span>
        </button>
      </div>

      {/* Semana badge — siempre en el DOM, entra/sale con fade + colapso de ancho */}
      <div style={{
        overflow: "hidden",
        maxWidth: showWeek ? "120px" : "0px",
        opacity: showWeek ? 1 : 0,
        transition: "max-width 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.2s ease",
        flexShrink: 0,
      }}>
        <div style={{
          background: C.primary, color: "#fff",
          borderRadius: 8, padding: "5px 12px",
          fontSize: 12, fontWeight: 800,
          letterSpacing: "0.04em",
          whiteSpace: "nowrap",
        }}>
          SEMANA {weekNum}
        </div>
      </div>

    </div>
  );
}

// ── Tab selector ─────────────────────────────────────────────────────────────
// Mismos pills que .laboral-exp-header-tabs / .laboral-exp-tab-btn en el
// detalle de conciliación/tribunal (activo = navy sólido, inactivo = gris claro).
function TabSelector({ value, onChange }) {
  const tabs = [
    { key: "resumen", label: "Resumen", icon: "fa-solid fa-chart-simple" },
    { key: "detalle", label: "Detalle", icon: "fa-solid fa-file-lines" },
  ];
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {tabs.map((t) => {
        const active = value === t.key;
        return (
          <button
            key={t.key}
            onClick={() => onChange(t.key)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "10px 22px", borderRadius: 10,
              fontSize: 13, fontWeight: 600,
              border: "none", cursor: "pointer",
              transition: "all 0.15s ease",
              fontFamily: "Manrope, sans-serif",
              background: active ? C.primary : "#f1f5f9",
              color: active ? "#fff" : "#475569",
              boxShadow: active ? "0 4px 14px rgba(10, 83, 154, 0.28)" : "none",
            }}
            onMouseEnter={(e) => {
              if (active) return;
              e.currentTarget.style.background = "#e2e8f0";
              e.currentTarget.style.color = "#334155";
            }}
            onMouseLeave={(e) => {
              if (active) return;
              e.currentTarget.style.background = "#f1f5f9";
              e.currentTarget.style.color = "#475569";
            }}
          >
            <i className={t.icon} style={{ fontSize: 14 }} />
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Card shell ────────────────────────────────────────────────────────────────
// El badge azul (.laboral-exp-icon-badge) solo va en los 3 encabezados
// principales (Centro de Conciliación / Tribunal Laboral / Desvinculaciones);
// las sub-cards (Audiencias, Cumplimientos, Términos, Solicitudes) llevan el
// icono simple, sin caja.
function SectionCard({ icon, title, badge = false, children }) {
  return (
    <div style={{
      background: C.white, borderRadius: 14,
      boxShadow: CARD_SHADOW,
      border: CARD_BORDER,
      overflow: "hidden",
    }}>
      <div style={{
        padding: "18px 20px 14px", display: "flex", alignItems: "center", gap: 10,
        marginBottom: 4, borderBottom: "1px solid #e2e8f0",
      }}>
        {badge ? (
          <div style={{
            width: 36, height: 36, borderRadius: 0,
            background: "#DCE9F5",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <i className={icon} style={{ fontSize: 16, color: "#0A539A" }} />
          </div>
        ) : icon ? (
          <span className="material-symbols-outlined" style={{ fontSize: 20, color: C.dark }}>{icon}</span>
        ) : null}
        <h2 style={{ fontSize: 17, fontWeight: 700, fontFamily: "Manrope,sans-serif", color: C.dark, margin: 0 }}>
          {title}
        </h2>
      </div>
      {children}
    </div>
  );
}

// ── Título de grupo (Centro de Conciliación / Tribunal Laboral / Desvinculaciones)
// en el tab Detalle — mismo badge azul que las cards, fuera de una card.
function GroupHeader({ icon, title, style }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, ...style }}>
      <div style={{
        width: 36, height: 36, borderRadius: 0, flexShrink: 0,
        background: "#DCE9F5",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <i className={icon} style={{ fontSize: 16, color: "#0A539A" }} />
      </div>
      <h3 style={{ fontSize: 16, fontWeight: 700, fontFamily: "Manrope,sans-serif", color: C.dark, margin: 0 }}>
        {title}
      </h3>
    </div>
  );
}

// ── Shared table head row ─────────────────────────────────────────────────────
function THead({ cols }) {
  return (
    <thead>
      <tr style={{ background: C.surfaceLow, borderBottom: `1px solid ${C.outline}20` }}>
        {cols.map((c, i) => (
          <th key={i} style={{
            padding: "9px 20px",
            fontSize: 10, fontWeight: 700,
            textTransform: "uppercase", letterSpacing: "0.1em",
            color: C.muted, textAlign: i === 0 ? "left" : "center",
          }}>{c}</th>
        ))}
      </tr>
    </thead>
  );
}

// ── td style ─────────────────────────────────────────────────────────────────
const td  = { padding: "13px 20px", verticalAlign: "middle" };
const tdc = { padding: "13px 20px", verticalAlign: "middle", textAlign: "center" };

// ─────────────────────────────────────────────────────────────────────────────
//  TAB 1 — RESUMEN
// ─────────────────────────────────────────────────────────────────────────────
function TabResumen({ summary, loading, onCityClick }) {
  if (loading) return <SkeletonGrid cols={2} />;
  if (!summary) return null;

  const ciudades = summary.ciudades ?? [];
  const totales  = summary.totales  ?? {};

  const CiudadCell = ({ c }) => (
    <td style={{ ...td, fontWeight: 600, fontSize: 14, color: C.dark }}>
      <span style={{
        display: "inline-block", width: 8, height: 8, borderRadius: "50%",
        background: cityColor(c.id_ciudad), marginRight: 8,
      }} />
      {c.nombre}
    </td>
  );

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>

      {/* ── Centro de Conciliación ─────────────────────────────────── */}
      <SectionCard icon="fa-solid fa-handshake" title="Centro de Conciliación" badge>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <THead cols={["Ciudad", "Audiencias Prejudiciales", "Cumplimientos de Pago"]} />
            <tbody>
              {ciudades.map((c) => (
                <tr
                  key={c.id_ciudad}
                  style={{ borderBottom: `1px solid ${C.outline}18` }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = C.surfaceLow)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <CiudadCell c={c} />
                  <td style={tdc}>
                    <CountPill
                      n={c.audiencias_conciliacion}
                      color={C.error} bg={C.errorSoft}
                      onClick={() => onCityClick(c.id_ciudad, "audiencias")}
                    />
                  </td>
                  <td style={tdc}>
                    <CountPill
                      n={c.convenios_pago}
                      color={C.primary} bg={C.primarySoft}
                      onClick={() => onCityClick(c.id_ciudad, "convenios")}
                    />
                  </td>
                </tr>
              ))}
              {/* Totales */}
              <tr style={{ borderTop: `2px solid ${C.outline}30` }}>
                <td style={{ ...td, fontWeight: 700, fontSize: 14, color: C.dark }}>Totales</td>
                <td style={{ ...tdc, fontWeight: 800, fontSize: 16, color: C.dark }}>
                  {totales.audiencias_conciliacion ?? 0}
                </td>
                <td style={{ ...tdc, fontWeight: 800, fontSize: 16, color: C.dark }}>
                  {totales.convenios_pago ?? 0}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* ── Tribunal Laboral ──────────────────────────────────────── */}
      <SectionCard icon="fa-solid fa-gavel" title="Tribunal Laboral" badge>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <THead cols={["Ciudad", "Audiencias", "Términos Críticos", "Cumplimientos de Pago"]} />
            <tbody>
              {ciudades.map((c) => (
                <tr
                  key={c.id_ciudad}
                  style={{ borderBottom: `1px solid ${C.outline}18` }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = C.surfaceLow)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <CiudadCell c={c} />
                  <td style={{ ...tdc, fontWeight: 700, fontSize: 15, color: c.audiencias_tribunal > 0 ? C.primary : C.muted }}>
                    {String(c.audiencias_tribunal).padStart(2, "0")}
                  </td>
                  <td style={{ ...tdc, fontWeight: 700, fontSize: 15, color: c.terminos_criticos > 0 ? C.error : C.muted }}>
                    {String(c.terminos_criticos).padStart(2, "0")}
                  </td>
                  <td style={tdc}>
                    <CountPill
                      n={c.convenios_pago_tribunal}
                      color={C.primary} bg={C.primarySoft}
                      onClick={() => onCityClick(c.id_ciudad, "convenios_tribunal")}
                    />
                  </td>
                </tr>
              ))}
              {/* Totales */}
              <tr style={{ borderTop: `2px solid ${C.outline}30` }}>
                <td style={{ ...td, fontWeight: 700, fontSize: 14, color: C.dark }}>Totales</td>
                <td style={{ ...tdc, fontWeight: 800, fontSize: 16, color: C.dark }}>
                  {totales.audiencias_tribunal ?? 0}
                </td>
                <td style={{ ...tdc, fontWeight: 800, fontSize: 16, color: C.dark }}>
                  {totales.terminos_criticos ?? 0}
                </td>
                <td style={{ ...tdc, fontWeight: 800, fontSize: 16, color: C.dark }}>
                  {totales.convenios_pago_tribunal ?? 0}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* ── Desvinculaciones ─────────────────────────────────────────── */}
      <div style={{ gridColumn: "1 / -1" }}>
      <SectionCard icon="fa-solid fa-user-minus" title="Desvinculaciones" badge>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <THead cols={["Ciudad", "Desvinculaciones"]} />
            <tbody>
              {ciudades.map((c) => (
                <tr
                  key={c.id_ciudad}
                  style={{ borderBottom: `1px solid ${C.outline}18` }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = C.surfaceLow)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <CiudadCell c={c} />
                  <td style={tdc}>
                    <CountPill
                      n={c.desvinculaciones}
                      color={C.amber} bg={C.amberSoft}
                      onClick={() => onCityClick(c.id_ciudad, "desvinculaciones")}
                    />
                  </td>
                </tr>
              ))}
              {/* Totales */}
              <tr style={{ borderTop: `2px solid ${C.outline}30` }}>
                <td style={{ ...td, fontWeight: 700, fontSize: 14, color: C.dark }}>Totales</td>
                <td style={{ ...tdc, fontWeight: 800, fontSize: 16, color: C.dark }}>
                  {totales.desvinculaciones ?? 0}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </SectionCard>
      </div>

    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  TAB 2 — DETALLE
// ─────────────────────────────────────────────────────────────────────────────
function TabDetalle({ data, loading, cityId, cities, onCityChange, navigate }) {
const audRows = uniqueByExpediente(
  data?.conciliacion_audiencias ?? []
);

const pagoRows = uniqueByExpediente(
  data?.convenios_pagos ?? []
);
  const trib       = data?.tribunal ?? {};
  const tribAud    = trib.audiencias ?? [];
  const tribTerm   = trib.terminos ?? [];
  const tribPagos  = trib.convenios_pagos ?? [];
  const desvRows   = data?.desvinculaciones ?? [];

  const goDetail = (id) => navigate(`/materias/laboral/centro-conciliacion/${id}`, { state: { from: "home" } });
  const goTribunal = (idTribunal) => navigate(`/materias/laboral/tribunal/${idTribunal}`, { state: { from: "home" } });

  return (
    <div>
      {/* City selector — solo si el usuario tiene más de una ciudad permitida */}
      {cities.length > 1 && (
        <div style={{
          display: "flex", justifyContent: "flex-end", marginBottom: 20,
        }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            background: `${cityId === ALL_CITIES ? C.primary : cityColor(cityId)}14`, padding: "7px 14px", borderRadius: 8,
            border: `1px solid ${cityId === ALL_CITIES ? C.primary : cityColor(cityId)}50`,
          }}>
            <span style={{
              display: "inline-block", width: 8, height: 8, borderRadius: "50%",
              background: cityId === ALL_CITIES ? C.primary : cityColor(cityId),
            }} />
            <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                           letterSpacing: "0.1em", color: C.muted }}>Ciudad:</span>
            <select
              value={cityId}
              onChange={(e) => {
                const v = e.target.value;
                onCityChange(v === ALL_CITIES ? ALL_CITIES : Number(v));
              }}
              style={{ background: "transparent", border: "none", outline: "none",
                       fontSize: 13, fontWeight: 700, color: cityId === ALL_CITIES ? C.primary : cityColor(cityId), cursor: "pointer" }}
            >
              <option value={ALL_CITIES}>Todos</option>
              {cities.map((c) => (
                <option key={c.id_ciudad} value={c.id_ciudad}>{c.nombre}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {loading && <SkeletonGrid cols={2} />}

      {!loading && (
        <div>
          {/* ── Centro de Conciliación ─── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <GroupHeader icon="fa-solid fa-handshake" title="Centro de Conciliación" style={{ marginBottom: 0 }} />
            <GroupHeader icon="fa-solid fa-gavel" title="Tribunal Laboral" style={{ marginBottom: 0 }} />

            {/* Audiencias */}
            <SectionCard title="Audiencias programadas">
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <THead cols={["Fecha/Hora", "Expediente", "Trabajador", "Empresa", "Estado"]} />
                  <tbody>
                    {audRows.length === 0 ? (
                      <tr><td colSpan={5} style={{ padding: "28px 20px", textAlign: "center", fontSize: 13, color: "#9aa2b1" }}>
                        Sin audiencias para este período
                      </td></tr>
                    ) : audRows.map((r, i) => (
                      <tr
                        key={r.id_conciliacion_audiencia ?? `prox-${r.id_conciliacion}-${i}`}
                        onClick={() => goDetail(r.id_conciliacion)}
                        style={{ borderBottom: `1px solid ${C.outline}1a`, cursor: "pointer", transition: "background 0.12s" }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = C.surfaceHigh)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <td style={td}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: C.dark }}>
                            {r.fecha_audiencia ? dayjs(r.fecha_audiencia).format("h:mm A") : "—"}
                          </div>
                          <div style={{ fontSize: 10, color: C.muted, marginTop: 1 }}>
                            {r.fecha_audiencia ? dayjs(r.fecha_audiencia).format("D MMM YYYY") : ""}
                          </div>
                         {
  r.tipo === "proxima" &&
  dayjs(r.fecha_audiencia).isSameOrAfter(dayjs(), "day") && (
    <div style={{ fontSize: 9, fontWeight: 700, color: C.primary, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 2 }}>
      Próxima
    </div>
  )
}
                          <ProximoBadge iso={r.fecha_audiencia} />
                        </td>
                        <td style={td}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: C.primary }}>
                            {r.expediente_format || r.expediente || `#${r.id_conciliacion}`}
                          </span>
                        </td>
                        <td style={td}><span style={{ fontSize: 13 }}>{titleCase(trabajadoresText(r)) || "—"}</span></td>
                        <td style={td}><span style={{ fontSize: 13 }}>{titleCase(r.nombre_empresa) || "—"}</span></td>
                        <td style={td}><StatusBadge status={r.status_nombre} /></td>
                      </tr>
                    ))}
                    {audRows.length > 0 && (
                      <tr style={{ borderTop: `2px solid ${C.outline}33`, background: C.surfaceHigh }}>
                        <td colSpan={5} style={{ padding: "10px 20px" }}>
                          <span style={{ fontSize: 11, color: C.muted, display: "flex", alignItems: "center", gap: 6 }}>
                            <span className="material-symbols-outlined" style={{ fontSize: 15, color: C.primary }}>event</span>
                            <span style={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: C.dark }}>
                              Audiencias Totales:
                            </span>
                            <span style={{ fontWeight: 800, fontSize: 13, color: C.primary }}>{audRows.length}</span>
                          </span>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </SectionCard>

            {/* Audiencias de tribunal */}
            <SectionCard title="Audiencias programadas">
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <THead cols={["Fecha/Hora", "Expediente", "Trabajador", "Empresa", "Etapa"]} />
                  <tbody>
                    {tribAud.length === 0 ? (
                      <tr><td colSpan={5} style={{ padding: "28px 20px", textAlign: "center", fontSize: 13, color: "#9aa2b1" }}>
                        Sin audiencias para este período
                      </td></tr>
                    ) : tribAud.map((r, i) => (
                      <tr
                        key={`${r.id_tribunal}-${i}`}
                        onClick={() => goTribunal(r.id_tribunal)}
                        style={{ borderBottom: `1px solid ${C.outline}1a`, cursor: "pointer", transition: "background 0.12s" }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = C.surfaceHigh)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <td style={td}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: C.dark }}>
                            {r.fecha_audiencia ? dayjs(r.fecha_audiencia).format("h:mm A") : "—"}
                          </div>
                          <div style={{ fontSize: 10, color: C.muted, marginTop: 1 }}>
                            {r.fecha_audiencia ? dayjs(r.fecha_audiencia).format("D MMM YYYY") : ""}
                          </div>
                          <ProximoBadge iso={r.fecha_audiencia} />
                        </td>
                        <td style={td}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: C.primary }}>
                            {r.numero_expediente || `#${r.id_tribunal}`}
                          </span>
                        </td>
                        <td style={td}><span style={{ fontSize: 13 }}>{titleCase(r.nombre_parte_actora) || "—"}</span></td>
                        <td style={td}><span style={{ fontSize: 13 }}>{titleCase(r.nombre_empresa) || "—"}</span></td>
                        <td style={td}><EtapaBadge label={r.etapa_label} /></td>
                      </tr>
                    ))}
                    {tribAud.length > 0 && (
                      <tr style={{ borderTop: `2px solid ${C.outline}33`, background: C.surfaceHigh }}>
                        <td colSpan={5} style={{ padding: "10px 20px" }}>
                          <span style={{ fontSize: 11, color: C.muted, display: "flex", alignItems: "center", gap: 6 }}>
                            <span className="material-symbols-outlined" style={{ fontSize: 15, color: C.primary }}>gavel</span>
                            <span style={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: C.dark }}>
                              Audiencias Totales:
                            </span>
                            <span style={{ fontWeight: 800, fontSize: 13, color: C.primary }}>{tribAud.length}</span>
                          </span>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </SectionCard>

            {/* Cumplimientos de pago */}
            <SectionCard title="Cumplimientos de pago">
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <THead cols={["Expediente", "Trabajador", "Empresa", "Monto", "Fecha de pago", "Estado"]} />
                  <tbody>
                    {pagoRows.length === 0 ? (
                      <tr><td colSpan={6} style={{ padding: "28px 20px", textAlign: "center", fontSize: 13, color: "#9aa2b1" }}>
                        Sin pagos para este período
                      </td></tr>
                    ) : pagoRows.map((r) => (
                      <tr
                        key={r.id_conciliacion_audiencia_convenio}
                        onClick={() => goDetail(r.id_conciliacion)}
                        style={{ borderBottom: `1px solid ${C.outline}1a`, cursor: "pointer", transition: "background 0.12s" }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = C.surfaceHigh)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <td style={td}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: C.primary }}>
                            {r.expediente_format || r.expediente || `#${r.id_conciliacion}`}
                          </span>
                        </td>
                        <td style={td}><span style={{ fontSize: 13 }}>{titleCase(trabajadoresText(r)) || "—"}</span></td>
                        <td style={td}><span style={{ fontSize: 13 }}>{titleCase(r.nombre_empresa) || "—"}</span></td>
                        <td style={td}><span style={{ fontSize: 13, fontWeight: 700 }}>{fmtMonto(r.monto)}</span></td>
                        <td style={tdc}>
                          {r.fecha_pago ? (
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 700, color: C.dark }}>
                                {dayjs(r.fecha_pago).format("h:mm A")}
                              </div>
                              <div style={{ fontSize: 10, color: C.muted, marginTop: 1 }}>
                                {dayjs(r.fecha_pago).format("D MMM YYYY")}
                              </div>
                            </div>
                          ) : (
                            <span style={{ fontSize: 13, color: C.muted }}>—</span>
                          )}
                        </td>
                        <td style={tdc}><PagoBadge iso={r.fecha_pago} /></td>
                      </tr>
                    ))}
                    {pagoRows.length > 0 && (
                      <tr style={{ borderTop: `2px solid ${C.outline}33`, background: C.surfaceHigh }}>
                        <td colSpan={6} style={{ padding: "10px 20px" }}>
                          <span style={{ fontSize: 11, color: C.muted, display: "flex", alignItems: "center", gap: 6 }}>
                            <span className="material-symbols-outlined" style={{ fontSize: 15, color: C.primary }}>receipt_long</span>
                            <span style={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: C.dark }}>
                            Pagos Totales:
                            </span>
                            <span style={{ fontWeight: 800, fontSize: 13, color: C.primary }}>{pagoRows.length}</span>
                          </span>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </SectionCard>

            {/* Términos críticos */}
            <SectionCard icon="event_note" title="Términos Críticos">
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <THead cols={["Expediente", "Trabajador", "Empresa", "Término", "Fecha límite", "Estado"]} />
                  <tbody>
                    {tribTerm.length === 0 ? (
                      <tr><td colSpan={6} style={{ padding: "28px 20px", textAlign: "center", fontSize: 13, color: "#9aa2b1" }}>
                        Sin términos críticos vigentes
                      </td></tr>
                    ) : tribTerm.map((r, i) => (
                      <tr
                        key={`${r.id_tribunal}-${i}`}
                        onClick={() => goTribunal(r.id_tribunal)}
                        style={{ borderBottom: `1px solid ${C.outline}1a`, cursor: "pointer", transition: "background 0.12s" }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = C.surfaceHigh)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <td style={td}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: C.primary }}>
                            {r.numero_expediente || `#${r.id_tribunal}`}
                          </span>
                        </td>
                        <td style={td}><span style={{ fontSize: 13 }}>{titleCase(r.nombre_parte_actora) || "—"}</span></td>
                        <td style={td}><span style={{ fontSize: 13 }}>{titleCase(r.nombre_empresa) || "—"}</span></td>
                        <td style={td}><span style={{ fontSize: 13 }}>{r.termino_label || "—"}</span></td>
                        <td style={tdc}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: C.dark }}>
                            {r.fecha_limite ? dayjs(r.fecha_limite).format("D MMM YYYY") : "—"}
                          </span>
                        </td>
                        <td style={tdc}><TermBadge iso={r.fecha_limite} /></td>
                      </tr>
                    ))}
                    {tribTerm.length > 0 && (
                      <tr style={{ borderTop: `2px solid ${C.outline}33`, background: C.surfaceHigh }}>
                        <td colSpan={6} style={{ padding: "10px 20px" }}>
                          <span style={{ fontSize: 11, color: C.muted, display: "flex", alignItems: "center", gap: 6 }}>
                            <span className="material-symbols-outlined" style={{ fontSize: 15, color: C.red }}>event_note</span>
                            <span style={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: C.dark }}>
                              Términos Totales:
                            </span>
                            <span style={{ fontWeight: 800, fontSize: 13, color: C.red }}>{tribTerm.length}</span>
                          </span>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </SectionCard>

            {/* Cumplimientos de pago de Tribunal (convenio activo) */}
            <div style={{ gridColumn: "1 / -1" }}>
            <SectionCard title="Cumplimientos de pago (Tribunal)">
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <THead cols={["Expediente", "Trabajador", "Empresa", "Monto", "Fecha de pago", "Estado"]} />
                  <tbody>
                    {tribPagos.length === 0 ? (
                      <tr><td colSpan={6} style={{ padding: "28px 20px", textAlign: "center", fontSize: 13, color: "#9aa2b1" }}>
                        Sin pagos para este período
                      </td></tr>
                    ) : tribPagos.map((r, i) => (
                      <tr
                        key={r.id_terminacion_parcialidad ?? `${r.id_tribunal}-unico-${i}`}
                        onClick={() => goTribunal(r.id_tribunal)}
                        style={{ borderBottom: `1px solid ${C.outline}1a`, cursor: "pointer", transition: "background 0.12s" }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = C.surfaceHigh)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <td style={td}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: C.primary }}>
                            {r.numero_expediente || `#${r.id_tribunal}`}
                          </span>
                        </td>
                        <td style={td}><span style={{ fontSize: 13 }}>{titleCase(r.nombre_parte_actora) || "—"}</span></td>
                        <td style={td}><span style={{ fontSize: 13 }}>{titleCase(r.nombre_empresa) || "—"}</span></td>
                        <td style={td}><span style={{ fontSize: 13, fontWeight: 700 }}>{fmtMonto(r.monto)}</span></td>
                        <td style={tdc}>
                          {r.fecha_pago ? (
                            <span style={{ fontSize: 13, fontWeight: 700, color: C.dark }}>
                              {dayjs(r.fecha_pago).format("D MMM YYYY")}
                            </span>
                          ) : (
                            <span style={{ fontSize: 13, color: C.muted }}>—</span>
                          )}
                        </td>
                        <td style={tdc}><PagoBadge iso={r.fecha_pago} /></td>
                      </tr>
                    ))}
                    {tribPagos.length > 0 && (
                      <tr style={{ borderTop: `2px solid ${C.outline}33`, background: C.surfaceHigh }}>
                        <td colSpan={6} style={{ padding: "10px 20px" }}>
                          <span style={{ fontSize: 11, color: C.muted, display: "flex", alignItems: "center", gap: 6 }}>
                            <span className="material-symbols-outlined" style={{ fontSize: 15, color: C.primary }}>receipt_long</span>
                            <span style={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: C.dark }}>
                              Pagos Totales:
                            </span>
                            <span style={{ fontWeight: 800, fontSize: 13, color: C.primary }}>{tribPagos.length}</span>
                          </span>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </SectionCard>
            </div>
          </div>


          {/* ── Desvinculaciones ─────────── */}
          <GroupHeader icon="fa-solid fa-user-minus" title="Desvinculaciones" style={{ marginTop: 32 }} />

          <SectionCard title="Solicitudes de baja">
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <THead cols={["Fecha de solicitud", "Solicitante", "Empleados", "Empresa"]} />
                <tbody>
                  {desvRows.length === 0 ? (
                    <tr><td colSpan={4} style={{ padding: "28px 20px", textAlign: "center", fontSize: 13, color: "#9aa2b1" }}>
                      Sin desvinculaciones para este período
                    </td></tr>
                  ) : desvRows.map((r) => (
                    <tr
                      key={r.id}
                      onClick={() => navigate(`/materias/laboral/desvinculaciones/${r.id}`, { state: { from: "home" } })}
                      style={{ borderBottom: `1px solid ${C.outline}1a`, cursor: "pointer", transition: "background 0.12s" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = C.surfaceHigh)}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <td style={td}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: C.dark }}>
                          {r.fecha_solicitacion ? dayjs(r.fecha_solicitacion).format("D MMM YYYY") : "—"}
                        </span>
                      </td>
                      <td style={td}><span style={{ fontSize: 13 }}>{titleCase(r.nombre_solicitante) || "—"}</span></td>
                      <td style={td}><span style={{ fontSize: 13 }}>{titleCase(r.empleados_str) || "—"}</span></td>
                      <td style={td}><span style={{ fontSize: 13 }}>{titleCase(r.nombre_empresa) || "—"}</span></td>
                    </tr>
                  ))}
                  {desvRows.length > 0 && (
                    <tr style={{ borderTop: `2px solid ${C.outline}33`, background: C.surfaceHigh }}>
                      <td colSpan={4} style={{ padding: "10px 20px" }}>
                        <span style={{ fontSize: 11, color: C.muted, display: "flex", alignItems: "center", gap: 6 }}>
                          <span className="material-symbols-outlined" style={{ fontSize: 15, color: C.amber }}>badge</span>
                          <span style={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: C.dark }}>
                            Desvinculaciones Totales:
                          </span>
                          <span style={{ fontWeight: 800, fontSize: 13, color: C.amber }}>{desvRows.length}</span>
                        </span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </SectionCard>
        </div>
      )}
    </div>
  );
}


// ── Loading skeleton ──────────────────────────────────────────────────────────
function SkeletonGrid({ cols }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 24 }}>
      {Array.from({ length: cols }).map((_, i) => (
        <div key={i} style={{
          background: C.white, borderRadius: 14, height: 260,
          borderTop: `3px solid ${C.outline}`,
          animation: "pulse 1.5s ease-in-out infinite",
        }} />
      ))}
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.5}}`}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  HOME — componente principal
// ─────────────────────────────────────────────────────────────────────────────
export default function Home() {
  const navigate = useNavigate();

  const [tab,          setTab]         = useState("detalle");
  const [viewMode,     setViewMode]    = useState("dia");
  const [selectedDate, setSelectedDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [cityId,       setCityId]      = useState(null);

  // Ciudades permitidas para el usuario logueado (administración ve las 4,
  // el resto solo las suyas — ver /home/ciudades en el backend)
  const [cities, setCities] = useState([]);
  const [citiesLoaded, setCitiesLoaded] = useState(false);

  // Resumen
  const [summary,        setSummary]        = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError,   setSummaryError]   = useState(null);

  // Detalle
  const [detail,        setDetail]        = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError,   setDetailError]   = useState(null);

  // Construye los query params de período
  const { desde, hasta } = getDateRange(selectedDate, viewMode);
  const periodParams = `desde=${desde}&hasta=${hasta}`;

  // Ciudades permitidas — una sola vez al montar
  useEffect(() => {
    api
      .get(`/home/ciudades`, { headers: authH() })
      .then((r) => {
        const list = r.data?.ciudades ?? [];
        setCities(list);
        setCityId((prev) =>
          prev === ALL_CITIES || list.some((c) => c.id_ciudad === prev)
            ? prev
            : (list.length > 1 ? ALL_CITIES : (list[0]?.id_ciudad ?? null))
        );
      })
      .catch(() => setCities([]))
      .finally(() => setCitiesLoaded(true));
  }, []);

  // Fetch resumen cuando cambia período o rango personalizado
  useEffect(() => {
    setSummaryLoading(true);
    setSummaryError(null);
    api
      .get(`/home/resumen?${periodParams}`, { headers: authH() })
      .then((r) => setSummary(r.data))
      .catch((e) => setSummaryError(e?.response?.data?.detail || e?.message))
      .finally(() => setSummaryLoading(false));
  }, [periodParams]);

  // Fetch detalle cuando cambia período, rango o ciudad (espera a saber qué ciudad puede ver)
  useEffect(() => {
    if (cityId == null) return;
    setDetailLoading(true);
    setDetailError(null);

    if (cityId === ALL_CITIES) {
      Promise.all(
        cities.map((c) =>
          api.get(`/home/panel?ciudad=${c.id_ciudad}&${periodParams}`, { headers: authH() }).then((r) => r.data)
        )
      )
        .then((results) => setDetail(mergePanelResults(results)))
        .catch((e) => setDetailError(e?.response?.data?.detail || e?.message))
        .finally(() => setDetailLoading(false));
      return;
    }

    api
      .get(`/home/panel?ciudad=${cityId}&${periodParams}`, { headers: authH() })
      .then((r) => setDetail(r.data))
      .catch((e) => setDetailError(e?.response?.data?.detail || e?.message))
      .finally(() => setDetailLoading(false));
  }, [cityId, periodParams, cities]);

  // Clic en pill del resumen → ir a detalle con esa ciudad
  const handleCityClick = (id) => {
    setCityId(id);
    setTab("detalle");
  };

  const error = tab === "resumen" ? summaryError : detailError;

  return (
    <main style={{ background: C.surface, minHeight: "calc(100vh - 56px)", padding: "32px 32px 48px" }}>
      <div style={{ width: "100%", margin: "0 auto" }}>

        {/* ── Header + Tabs (una sola card, como en el detalle de conciliación) ── */}
        <div style={{
          background: C.white, borderRadius: 14,
          boxShadow: CARD_SHADOW, border: CARD_BORDER,
          padding: "24px 24px 0", marginBottom: 24,
        }}>
          <div style={{
            display: "flex", alignItems: "flex-end",
            justifyContent: "space-between", flexWrap: "wrap", gap: 12,
            paddingBottom: 20, borderBottom: "1px solid #e2e8f0",
          }}>
            <div>
              <h1 style={{
                fontSize: 28, fontWeight: 650,
                letterSpacing: "-0.01em", color: "#0f172a", margin: 0, lineHeight: 1.2,
              }}>
                Panel de Inicio
              </h1>
              <p style={{ fontSize: 13, color: C.muted, marginTop: 4, fontWeight: 500 }}>
                {capitalizeDateWords(`${dayjs(desde).format("D MMM")} — ${dayjs(hasta).format("D MMM YYYY")}`)}
              </p>
            </div>
            <DateNavBar
              date={selectedDate}
              mode={viewMode}
              onDateChange={setSelectedDate}
              onModeChange={setViewMode}
            />
          </div>

          <div style={{ padding: "18px 0 20px" }}>
            <TabSelector value={tab} onChange={setTab} />
          </div>
        </div>

        {/* ── Error ──────────────────────────────────────────────────── */}
        {error && (
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "12px 16px", background: "#ffdad6",
            borderRadius: 8, marginBottom: 20,
          }}>
            <span className="material-symbols-outlined" style={{ color: C.error, fontSize: 18 }}>error</span>
            <span style={{ color: C.error, fontSize: 13, fontWeight: 600 }}>{error}</span>
          </div>
        )}

        {/* ── Contenido ──────────────────────────────────────────────── */}
        {citiesLoaded && cities.length === 0 ? (
          <div style={{
            padding: "24px 20px", background: C.surfaceLow,
            borderRadius: 10, border: `1px solid ${C.outline}40`,
            color: C.muted, fontSize: 13, fontWeight: 600,
          }}>
            No tienes ninguna ciudad asignada. Contacta a un administrador para que te dé acceso.
          </div>
        ) : tab === "resumen" ? (
          <TabResumen
            summary={summary}
            loading={summaryLoading}
            onCityClick={handleCityClick}
          />
        ) : (
          <TabDetalle
            data={detail}
            loading={detailLoading || cityId == null}
            cityId={cityId}
            cities={cities}
            onCityChange={setCityId}
            navigate={navigate}
          />
        )}

      </div>
    </main>
  );
}
